// src/components/AuthPage.js
import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabaseClient'

const CITIES = [
  { key: 'nyc',    name: 'New York',  system: 'MTA',        color: '#EE352E', shape: 'circle'   },
  { key: 'paris',  name: 'Paris',     system: 'RATP',       color: '#5F2D8A', shape: 'circle'   },
  { key: 'london', name: 'London',    system: 'TfL',        color: '#E32017', shape: 'ring'     },
  { key: 'milan',  name: 'Milan',     system: 'ATM',        color: '#CC0000', shape: 'diamond'  },
  { key: 'berlin', name: 'Berlin',    system: 'BVG',        color: '#F0CA00', shape: 'square'   },
  { key: 'madrid', name: 'Madrid',    system: 'Metro',      color: '#E4032E', shape: 'triangle' },
]

const M_PATH = 'M212.25,205.15V81.5c0-7.09-3.9-14.53-15.6-14.53-8.85,0-12.4,3.9-16.29,11.69l-38.27,79.73h-.35L103.11,78.66C99.21,70.87,95.67,67,86.81,67,75.12,67,71.22,74.41,71.22,81.5V205.15c0,6.74,5.32,10.64,11.7,10.64,5.66,0,12-3.9,12-10.64V113h.36L130.4,184.6c2.47,5,5.67,7.8,11.34,7.8s8.85-2.84,11.33-7.8L188.15,113h.36v92.12c0,6.74,6.37,10.64,12.05,10.64,6.37,0,11.69-3.9,11.69-10.64'

function StationShape({ shape, color, size = 14 }) {
  if (shape === 'circle')   return <div style={{ width: size, height: size, borderRadius: '50%', background: color }} />
  if (shape === 'ring')     return <div style={{ width: size, height: size, borderRadius: '50%', border: `3px solid ${color}`, background: 'transparent' }} />
  if (shape === 'diamond')  return <div style={{ width: size - 2, height: size - 2, background: color, transform: 'rotate(45deg)' }} />
  if (shape === 'square')   return <div style={{ width: size - 1, height: size - 1, background: color }} />
  if (shape === 'triangle') return <div style={{ width: 0, height: 0, borderLeft: `${size/2}px solid transparent`, borderRight: `${size/2}px solid transparent`, borderBottom: `${size}px solid ${color}` }} />
  return null
}

export default function AuthPage({ theme, toggleTheme }) {
  const [mode, setMode]       = useState('login')
  const [email, setEmail]     = useState('')
  const [password, setPassword] = useState('')
  const [name, setName]       = useState('')
  const [loading, setLoading] = useState(null)
  const [message, setMessage] = useState(null)
  const [error, setError]     = useState(null)
  const [activeIdx, setActiveIdx] = useState(0)
  const [trainLeft, setTrainLeft] = useState(null)
  const metroRef  = useRef(null)
  const stationRefs = useRef([])
  const intervalRef = useRef(null)

  // Position train over active station
  useEffect(() => {
    const updateTrain = () => {
      const metro = metroRef.current
      const station = stationRefs.current[activeIdx]
      if (!metro || !station) return
      const mr = metro.getBoundingClientRect()
      const sr = station.getBoundingClientRect()
      setTrainLeft((sr.left - mr.left) + sr.width / 2)
    }
    updateTrain()
    window.addEventListener('resize', updateTrain)
    return () => window.removeEventListener('resize', updateTrain)
  }, [activeIdx])

  // Auto-cycle cities
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setActiveIdx(i => (i + 1) % CITIES.length)
    }, 2000)
    return () => clearInterval(intervalRef.current)
  }, [])

  const handleStationClick = (i) => {
    clearInterval(intervalRef.current)
    setActiveIdx(i)
  }

  const clear = () => { setMessage(null); setError(null) }
  const city = CITIES[activeIdx]

  const googleLogin = async () => {
    setLoading('google'); clear()
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google', options: { redirectTo: window.location.origin + '/app.html' }
    })
    if (error) { setError(error.message); setLoading(null) }
  }

  const handleLogin = async (e) => {
    e.preventDefault(); setLoading('email'); clear()
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) setError(error.message)
    setLoading(null)
  }

  const handleSignup = async (e) => {
    e.preventDefault(); setLoading('email'); clear()
    const { error } = await supabase.auth.signUp({ email, password, options: { data: { full_name: name } } })
    if (error) setError(error.message)
    else setMessage('Check your email to confirm your account.')
    setLoading(null)
  }

  const handleReset = async (e) => {
    e.preventDefault(); setLoading('email'); clear()
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/reset` })
    if (error) setError(error.message)
    else setMessage('Password reset link sent — check your email.')
    setLoading(null)
  }

  const onSubmit = mode === 'login' ? handleLogin : mode === 'signup' ? handleSignup : handleReset

  return (
    <div className="auth4-page">
      <div className="auth4-wrap">

        {/* ── LEFT: Brand + Metro ── */}
        <div className="auth4-brand">
          <div className="auth4-stripes" aria-hidden="true" />

          <div className="auth4-mark-row">
            <div className="auth4-mark">
              <svg width="22" height="22" viewBox="0 0 284 284">
                <path fill="currentColor" d={M_PATH} />
              </svg>
            </div>
            <button className="auth4-theme-btn" onClick={toggleTheme}>
              {theme === 'dark' ? '☀️' : '🌑'}
            </button>
          </div>

          <p className="auth4-eyebrow">Rhonda Byrne's</p>
          <h1 className="auth4-title">The Magic</h1>
          <p className="auth4-tagline">
            A 28-station gratitude journey.<br />Choose a line. One station per day.
          </p>

          {/* Metro line widget */}
          <div className="auth4-metro-wrap">
            <div className="auth4-metro-header">
              <span className="auth4-metro-label">LINES</span>
              <span className="auth4-city-name">
                <StationShape shape={city.shape} color={city.color} size={12} />
                <span style={{ color: city.color, transition: 'color 0.4s' }}>{city.name}</span>
                <span className="auth4-city-system">{city.system}</span>
              </span>
            </div>

            {/* Track */}
            <div className="auth4-metro" ref={metroRef}>
              <div className="auth4-track" />

              {CITIES.map((c, i) => (
                <div
                  key={c.key}
                  ref={el => stationRefs.current[i] = el}
                  className={'auth4-station' + (i === activeIdx ? ' active' : '')}
                  onClick={() => handleStationClick(i)}
                  title={c.name}
                >
                  <StationShape shape={c.shape} color={c.color} size={13} />
                </div>
              ))}

              {/* Animated train */}
              {trainLeft !== null && (
                <div
                  className="auth4-train"
                  style={{ left: trainLeft, '--train-color': city.color }}
                >
                  <div className="auth4-train-dot" style={{ background: city.color }} />
                </div>
              )}
            </div>

            {/* Pills */}
            <div className="auth4-pills">
              <span className="auth4-pill">6 Cities</span>
              <span className="auth4-pill">28 Stations</span>
              <span className="auth4-pill">10 Gratitudes</span>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Auth form ── */}
        <div className="auth4-panel">

          {mode !== 'reset' ? (
            <div className="auth4-tabs">
              <button className={'auth4-tab' + (mode === 'login' ? ' active' : '')}
                onClick={() => { setMode('login'); clear() }}>Sign In</button>
              <button className={'auth4-tab' + (mode === 'signup' ? ' active' : '')}
                onClick={() => { setMode('signup'); clear() }}>Sign Up</button>
            </div>
          ) : (
            <div className="auth4-tabs">
              <button className="auth4-tab active">Reset Password</button>
            </div>
          )}

          {mode !== 'reset' && (
            <button className="auth4-google" onClick={googleLogin} disabled={!!loading}>
              <svg width="17" height="17" viewBox="0 0 18 18" style={{ flexShrink: 0 }}>
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"/>
              </svg>
              {loading === 'google' ? 'Connecting…' : 'Continue with Google'}
            </button>
          )}

          {mode !== 'reset' && <div className="auth4-divider"><span>or</span></div>}

          <form onSubmit={onSubmit} className="auth4-form">
            {mode === 'signup' && (
              <div className="auth4-field">
                <label className="auth4-label">Full Name</label>
                <input className="auth4-input" type="text" placeholder="Your name"
                  value={name} onChange={e => setName(e.target.value)} required />
              </div>
            )}
            <div className="auth4-field">
              <label className="auth4-label">Email</label>
              <input className="auth4-input" type="email" placeholder="you@example.com"
                value={email} onChange={e => setEmail(e.target.value)} required />
            </div>
            {mode !== 'reset' && (
              <div className="auth4-field">
                <label className="auth4-label">Password</label>
                <input className="auth4-input" type="password" placeholder="••••••••"
                  value={password} onChange={e => setPassword(e.target.value)} required minLength={6} />
              </div>
            )}
            {error   && <div className="auth4-error">{error}</div>}
            {message && <div className="auth4-msg">{message}</div>}
            <button className="auth4-submit" type="submit" disabled={!!loading}
              style={{ background: city.color, transition: 'background 0.4s, opacity 0.2s' }}>
              {loading === 'email' ? 'Please wait…'
                : mode === 'login'  ? 'Sign In'
                : mode === 'signup' ? 'Create Account'
                : 'Send Reset Link'}
            </button>
          </form>

          <div className="auth4-footer">
            {mode === 'login' && (
              <button className="auth4-link" onClick={() => { setMode('reset'); clear() }}>Forgot password?</button>
            )}
            {mode === 'reset' && (
              <button className="auth4-link" onClick={() => { setMode('login'); clear() }}>← Back to sign in</button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
